import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  Paper
} from '@mui/material';
import { Close } from '@mui/icons-material';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { GalleryMediaItem, CMSViewMode } from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';

interface CMSConsumerFeaturedGalleryProps {
  featuredCollection: any; // The featured collection data from CMS
  hasVideo?: boolean;
  invert?: boolean;
  marginTop?: number;
  marginBottom?: number;
  viewMode?: CMSViewMode;
  // Optional modal behavior
  useModal?: boolean;
  modalUrl?: string;
  catalogueUrl?: string;
}

const CMSConsumerFeaturedGallery: React.FC<CMSConsumerFeaturedGalleryProps> = ({
                                                                                 featuredCollection,
                                                                                 hasVideo = false,
                                                                                 invert = false,
                                                                                 marginTop = 5,
                                                                                 marginBottom = 5,
                                                                                 viewMode = 'live',
                                                                                 useModal = false,
                                                                                 modalUrl,
                                                                                 catalogueUrl
                                                                               }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  // Extract data from featured collection
  const title = featuredCollection?.page_title || '';
  const description = featuredCollection?.description || '';
  const images = featuredCollection?.gallery_media || [];
  const categoryId = featuredCollection?.category_id;

  // Find image by position
  const findImageByPosition = (position: string) => {
    const image = images.find((img: GalleryMediaItem) => img.position === position);
    return image?.file_link;
  };

  // Handle click navigation
  const handleClick = async () => {
    if (useModal && modalUrl) {
      // Open modal for international brands
      setModalOpen(true);
    } else if (catalogueUrl) {
      // Navigate to catalogue URL (external link)
      window.open(catalogueUrl, '_blank');
    } else if (categoryId) {
      // Navigate using CMS category data for consumer site
      try {
        const categoryResponse = await getCategoryDetails(categoryId, viewMode);
        if (categoryResponse.success && categoryResponse.cms_category) {
          const categoryUrl = buildCategoryUrl(categoryResponse.cms_category);
          navigate(categoryUrl);
        }
      } catch (error) {
        console.error('Error navigating to featured collection:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const showClickableCursor = (useModal && modalUrl) || catalogueUrl || categoryId;

  // Show missing content if no data
  if (!featuredCollection || !images || images.length === 0) {
    return <MissingContent sectionName="Featured Collection" />;
  }

  return (
    <>
      {/* Desktop View */}
      <Container
        maxWidth="lg"
        className="grid-lg"
        sx={{ cursor: showClickableCursor ? 'pointer' : 'default' }}
        onClick={handleClick}
      >
        <Grid mt={marginTop || 5} mb={marginBottom || 5} container spacing={2}>
          <Grid id="inner-grid" item xs={6}>
            <Box overflow="hidden" sx={{}}>
              <img
                style={{
                  width: '100%',
                  height: '80vh',
                  objectFit: 'cover',
                  padding: '1rem',
                }}
                src={findImageByPosition('top left')}
                alt="featured-0"
              />
            </Box>
            {invert === true ? (
              <Box
                sx={{
                  width: '100%',
                  height: '40vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {hasVideo ? (
                  <video
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    autoPlay
                    muted
                    loop
                  >
                    <source src={''} type="video/mp4"></source>
                  </video>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: { xs: '90%', lg: '100%' },
                      height: '100%',
                    }}
                  >
                    <Typography variant="h4">{title}</Typography>
                    <Typography textAlign="center">
                      {description}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                overflow="hidden"
                sx={{
                  marginTop: '15px',
                }}
              >
                <img
                  style={{
                    width: '100%',
                    height: '40vh',
                    objectFit: 'cover',
                    paddingRight: '1rem',
                    paddingLeft: '1rem',
                  }}
                  src={findImageByPosition('bottom left')}
                  alt="featured-1"
                />
              </Box>
            )}
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              padding: '5px',
            }}
          >
            {invert === true ? (
              <Box overflow="hidden" height="600px">
                <img
                  style={{
                    width: '100%',
                    height: '40vh',
                    objectFit: 'cover',
                  }}
                  src={findImageByPosition('bottom right')}
                  alt="featured-2"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {hasVideo ? (
                  <video
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    autoPlay
                    muted
                    loop
                  >
                    <source src={''} type="video/mp4"></source>
                  </video>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: { xs: '90%', lg: '100%' },
                      height: '40vh',
                      gap: '19px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: '10px 12px 10px 12px',
                        background: '#EDEDED',
                      }}
                    >
                      <Typography
                        textTransform={'uppercase'}
                        fontSize={'12px'}
                      >
                        Featured Collection
                      </Typography>
                    </Box>
                    <Typography
                      variant="h1"
                      fontSize={'46px'}
                      fontWeight={'500'}
                      lineHeight={'32px'}
                    >
                      {title}
                    </Typography>
                    <Typography
                      textAlign="center"
                      fontSize={'16px'}
                      fontWeight={'400'}
                      lineHeight={'24px'}
                    >
                      {description}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box overflow="hidden" marginTop="15px">
              <img
                style={{
                  width: '100%',
                  height: '80vh',
                  marginTop: '3px',
                  objectFit: 'cover',
                }}
                src={findImageByPosition('bottom right')}
                alt={`featured-2`}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Mobile View */}
      <Grid
        className="grid-sm"
        container
        flexDirection="column"
        display={'flex'}
        justifyContent={'center'}
        alignItems="center"
        mt={marginTop || 5}
        mb={marginBottom || 5}
        paddingX="16px"
        sx={{
          display: { xs: 'flex', lg: 'none' }, // Only show on mobile
        }}
      >
        <Box
          sx={{
            maxWidth: '100%',
          }}
        >
          <Box>
            <img
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover',
              }}
              src={findImageByPosition('bottom right')}
              alt="featured-2"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              width: '100%',
              height: 'auto',
              pt: '1rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: '10px 12px 10px 12px',
                background: '#EDEDED',
              }}
            >
              <Typography textTransform={'uppercase'} fontSize={'12px'}>
                Featured Collection
              </Typography>
            </Box>
            <Typography
              mt={3}
              variant="h5"
              fontSize={'40px'}
              fontWeight={'400'}
            >
              {title}
            </Typography>
            <Typography
              fontSize={'16px'}
              mt={2}
              sx={{
                width: '100%',
              }}
            >
              {description}
            </Typography>
            <Button
              sx={{
                mt: 3,
                mb: 5,
                borderRadius: '0',
                padding: '1rem 3rem',
                backgroundColor: '#fff',
              }}
              variant="outlined"
              color="primary"
              onClick={handleClick}
            >
              {useModal ? 'View Collection' : catalogueUrl ? 'View Catalogue' : 'Explore The Collection'}
            </Button>
          </Box>
        </Box>
      </Grid>

      {/* Modal for International Brands */}
      {useModal && (
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="featured-collection-modal"
          aria-describedby="featured-collection-viewer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper
            sx={{
              position: 'relative',
              width: '95vw',
              height: '90vh',
              maxWidth: '1200px',
              maxHeight: '800px',
              outline: 'none',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 24,
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                },
              }}
            >
              <Close />
            </IconButton>

            {/* Iframe Container */}
            {modalUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  paddingTop: 'max(60%, 326px)',
                }}
              >
                <iframe
                  allow="clipboard-write"
                  sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms"
                  allowFullScreen={true}
                  style={{
                    position: 'absolute',
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                  }}
                  src={modalUrl}
                  title={`Featured Collection - ${title}`}
                />
              </Box>
            )}
          </Paper>
        </Modal>
      )}
    </>
  );
};

export default CMSConsumerFeaturedGallery;